import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPlacementCalls, fetchQueueBoard, } from '@/entities/queue-placement';
import { assignBmAction } from '@/features/assign-bm';
import { placeNewAgentAction } from '@/features/place-new-queue-agent';
import { QueueCallHistory } from '@/features/play-queue-call-recording';
import { closePlacementAction, deferPlacementAction, recordContactAction, } from '@/features/record-contact';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { agentCountLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, Sheet, Skeleton, } from '@/shared/ui';
import { BrandManagerPicker } from '@/widgets/brand-manager-picker';
import { CreateQueueAgentForm } from './create-queue-agent-form';
function statusLabel(status) {
    switch (status) {
        case 'PENDING':
            return 'Ждёт звонка';
        case 'CONTACTED':
            return 'Уже говорили';
        case 'DEFERRED':
            return 'Отложили';
        default:
            return status;
    }
}
function filterPlacements(placements, bmFilter) {
    if (bmFilter === 'all') {
        return placements;
    }
    return placements.filter((placement) => placement.assignedManagerId == null);
}
export function QueueBoard() {
    const { scope } = useAgentScope();
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bmFilter, setBmFilter] = useState('all');
    const [selected, setSelected] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pickingBm, setPickingBm] = useState(false);
    const [calls, setCalls] = useState([]);
    const [callsLoading, setCallsLoading] = useState(false);
    const [callsRefreshing, setCallsRefreshing] = useState(false);
    const [callsCheckedAt, setCallsCheckedAt] = useState(null);
    const [callsError, setCallsError] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchQueueBoard(scope);
            setColumns(data.columns);
            setSelected((current) => {
                if (!current)
                    return null;
                const next = data.columns
                    .flatMap((column) => column.placements)
                    .find((item) => item.placementId === current.placementId);
                return next ?? null;
            });
        }
        catch (err) {
            const message = err instanceof ApiError ? err.message : uiCopy.loadFailed;
            setError(message);
        }
        finally {
            setLoading(false);
        }
    }, [scope]);
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        if (!selected || sheet !== 'placement') {
            setCalls([]);
            setCallsError(null);
            setCallsLoading(false);
            setCallsRefreshing(false);
            setCallsCheckedAt(null);
            return;
        }
        let cancelled = false;
        let timer = 0;
        setCallsLoading(true);
        setCallsError(null);
        setCallsCheckedAt(null);
        function needsFastPoll(items) {
            return items.some((item) => {
                if (item.recordingStatus === 'unknown') {
                    return true;
                }
                if (item.recordingStatus !== 'available') {
                    return false;
                }
                if (!item.transcription) {
                    return true;
                }
                return (item.transcription.status === 'queued' ||
                    item.transcription.status === 'processing');
            });
        }
        async function loadCalls(showLoading) {
            if (showLoading) {
                setCallsLoading(true);
            }
            else {
                setCallsRefreshing(true);
            }
            try {
                const data = await fetchPlacementCalls(selected.placementId);
                if (cancelled) {
                    return null;
                }
                setCalls(data.items);
                setCallsError(null);
                setCallsCheckedAt(new Date());
                return data.items;
            }
            catch (err) {
                if (cancelled) {
                    return null;
                }
                const message = err instanceof ApiError ? err.message : 'Не получилось загрузить звонки';
                setCallsError(message);
                return null;
            }
            finally {
                if (!cancelled && showLoading) {
                    setCallsLoading(false);
                }
                if (!cancelled) {
                    setCallsRefreshing(false);
                }
            }
        }
        function schedule(items) {
            const delay = items && needsFastPoll(items) ? 2000 : 15000;
            timer = window.setTimeout(() => {
                void loadCalls(false).then((next) => {
                    if (!cancelled) {
                        schedule(next);
                    }
                });
            }, delay);
        }
        void loadCalls(true).then((items) => {
            if (!cancelled) {
                schedule(items);
            }
        });
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [selected, sheet]);
    async function runAction(action) {
        setBusy(true);
        setError(null);
        try {
            await action();
            setSelected(null);
            setSheet(null);
            await load();
        }
        catch (err) {
            const message = err instanceof ApiError ? err.message : uiCopy.actionFailed;
            setError(message);
        }
        finally {
            setBusy(false);
        }
    }
    const visibleColumns = useMemo(() => columns.map((column) => ({
        ...column,
        placements: filterPlacements(column.placements, bmFilter),
    })), [columns, bmFilter]);
    if (loading) {
        return (_jsxs("div", { className: "space-y-4", "aria-busy": "true", children: [_jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(Skeleton, { className: "h-8 w-16" }), _jsx(Skeleton, { className: "h-8 w-20" })] }), _jsx("div", { className: "flex gap-3 overflow-x-auto pb-2", children: Array.from({ length: 5 }).map((_, columnIndex) => (_jsxs("section", { className: "min-w-[220px] flex-1 rounded-md border border-border bg-card p-3", children: [_jsxs("header", { className: "mb-3 space-y-2", children: [_jsx(Skeleton, { className: "h-4 w-28" }), _jsx(Skeleton, { className: "h-3 w-16" })] }), _jsx("ul", { className: "space-y-2", children: Array.from({ length: 3 }).map((_, cardIndex) => (_jsx("li", { children: _jsx(Skeleton, { className: "h-16 w-full rounded-md" }) }, cardIndex))) })] }, columnIndex))) })] }));
    }
    const empty = columns.every((column) => column.placements.length === 0);
    const sheetTitle = sheet === 'create-agent'
        ? 'Создать агента'
        : (selected?.agentDisplayName ?? 'Агент');
    return (_jsxs("div", { className: "space-y-4", children: [error && sheet === null ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [!empty ? (_jsxs("div", { className: "flex flex-wrap gap-2", role: "group", "aria-label": uiCopy.queuesBmFilterLabel, children: [_jsx(Button, { type: "button", size: "sm", variant: bmFilter === 'all' ? 'default' : 'outline', "aria-pressed": bmFilter === 'all', onClick: () => setBmFilter('all'), children: uiCopy.queuesBmFilterAll }), _jsx(Button, { type: "button", size: "sm", variant: bmFilter === 'unassigned' ? 'default' : 'outline', "aria-pressed": bmFilter === 'unassigned', onClick: () => setBmFilter('unassigned'), children: uiCopy.queuesBmFilterUnassigned })] })) : null, _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: () => {
                            setError(null);
                            setSelected(null);
                            setPickingBm(false);
                            setSheet('create-agent');
                        }, children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0430\u0433\u0435\u043D\u0442\u0430" })] }), empty ? (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u041F\u043E\u043A\u0430 \u043D\u0438\u043A\u043E\u0433\u043E \u043D\u0435\u0442" }), _jsx(AlertDescription, { children: "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u0430\u0433\u0435\u043D\u0442\u0430 \u0438 \u043F\u043E\u0441\u0442\u0430\u0432\u044C\u0442\u0435 \u0435\u0433\u043E \u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u044C." })] })) : null, _jsx("div", { className: "flex gap-3 overflow-x-auto pb-2", children: visibleColumns.map((column) => (_jsxs("section", { className: "min-w-[220px] flex-1 rounded-md border border-border bg-card p-3", children: [_jsxs("header", { className: "mb-3", children: [_jsx("h2", { className: "text-sm font-semibold", children: column.title }), _jsx("p", { className: "text-xs text-muted-foreground", children: agentCountLabel(column.placements.length) })] }), _jsx("ul", { className: "space-y-2", children: column.placements.map((placement) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "w-full rounded-md border border-border bg-background px-3 py-2 text-left hover:bg-accent", onClick: () => {
                                        setCalls([]);
                                        setCallsError(null);
                                        setCallsLoading(true);
                                        setPickingBm(false);
                                        setError(null);
                                        setSelected(placement);
                                        setSheet('placement');
                                    }, children: [_jsx("div", { className: "text-sm font-medium", children: placement.agentDisplayName }), _jsx("div", { className: "text-xs text-muted-foreground", children: statusLabel(placement.status) }), placement.assignedManagerName ? (_jsx("div", { className: "text-xs text-muted-foreground", children: placement.assignedManagerName })) : (_jsx("div", { className: "text-xs font-medium text-amber-700 dark:text-amber-400", children: uiCopy.bmAssignmentNone }))] }) }, placement.placementId))) })] }, column.queueId))) }), _jsxs(Sheet, { open: sheet !== null, onClose: () => {
                    setPickingBm(false);
                    setSelected(null);
                    setSheet(null);
                    setError(null);
                }, title: sheetTitle, children: [error && sheet !== null ? (_jsxs(Alert, { variant: "destructive", className: "mb-4", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error })] })) : null, sheet === 'create-agent' ? (_jsx(CreateQueueAgentForm, { columns: columns, defaultQueueId: columns[0]?.queueId ?? '', busy: busy, onSubmit: (input) => runAction(async () => {
                            await placeNewAgentAction(input);
                        }) })) : null, sheet === 'placement' && selected ? (_jsxs("div", { className: "space-y-4", children: [_jsxs("p", { className: "text-sm text-muted-foreground", children: ["\u0421\u0435\u0439\u0447\u0430\u0441: ", statusLabel(selected.status)] }), _jsx("p", { className: "text-sm", children: selected.assignedManagerName
                                    ? `Бренд-менеджер: ${selected.assignedManagerName}`
                                    : uiCopy.bmAssignmentNone }), selected.reason ? (_jsxs("p", { className: "text-sm", children: ["\u041F\u043E\u0447\u0435\u043C\u0443 \u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u0438: ", selected.reason] })) : null, selected.recommendedAction ? (_jsxs("p", { className: "text-sm", children: ["\u0427\u0442\u043E \u0441\u0434\u0435\u043B\u0430\u0442\u044C: ", selected.recommendedAction] })) : null, _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Link, { className: "inline-flex h-11 items-center justify-center rounded-md border border-input px-4 text-sm", to: `/agents/${selected.agentId}`, children: uiCopy.openAgentCard }), pickingBm ? (_jsx(BrandManagerPicker, { busy: busy, excludeManagerId: selected.assignedManagerId ?? undefined, title: uiCopy.pickBmAssignment, onPick: (manager) => {
                                            void (async () => {
                                                setBusy(true);
                                                setError(null);
                                                try {
                                                    await assignBmAction(selected.agentId, manager.managerId);
                                                    setPickingBm(false);
                                                    await load();
                                                }
                                                catch (err) {
                                                    const message = err instanceof ApiError
                                                        ? err.message
                                                        : uiCopy.actionFailed;
                                                    setError(message);
                                                }
                                                finally {
                                                    setBusy(false);
                                                }
                                            })();
                                        } })) : (_jsx(Button, { type: "button", variant: "secondary", disabled: busy, onClick: () => setPickingBm(true), children: selected.assignedManagerId
                                            ? uiCopy.changeBmAction
                                            : uiCopy.assignBmAction })), _jsx(Button, { type: "button", disabled: busy, onClick: () => void runAction(() => recordContactAction(selected.placementId)), children: "\u041A\u043E\u043D\u0442\u0430\u043A\u0442 \u0441\u043E\u0441\u0442\u043E\u044F\u043B\u0441\u044F" }), _jsx(Button, { type: "button", variant: "secondary", disabled: busy, onClick: () => void runAction(() => deferPlacementAction(selected.placementId)), children: "\u041E\u0442\u043B\u043E\u0436\u0438\u0442\u044C" }), _jsx(Button, { type: "button", variant: "destructive", disabled: busy, onClick: () => void runAction(() => closePlacementAction(selected.placementId)), children: "\u0423\u0431\u0440\u0430\u0442\u044C \u0438\u0437 \u043E\u0447\u0435\u0440\u0435\u0434\u0438" })] }), _jsx(QueueCallHistory, { placementId: selected.placementId, calls: calls, loading: callsLoading, refreshing: callsRefreshing, checkedAt: callsCheckedAt, error: callsError })] })) : null] })] }));
}
